import React from 'react'
import { Form } from 'react-bootstrap'
import { type StoredApiKey } from '../../../models/ExternalDataSource'
import { DOCS_LINKS, MAX_DESCRIPTION_LENGTH, MAX_TIMEOUT_MS, MIN_TIMEOUT_MS, SUPPORTED_CONTRACT_VERSIONS } from '../constants'
import { DataTypesInput } from './DataTypesInput'
import { type DataSourceFormValues, isAuthValid, isDescriptionValid, isHttpsUrl, isTimeoutValid } from './dataSourceForm'

interface DataSourceFormFieldsProps {
  values: DataSourceFormValues
  onChange: (values: DataSourceFormValues) => void
  validated: boolean
  storedApiKey?: StoredApiKey
}

export const DataSourceFormFields: React.FC<DataSourceFormFieldsProps> = ({ values, onChange, validated, storedApiKey = null }) => {
  const set = <K extends keyof DataSourceFormValues>(key: K, value: DataSourceFormValues[K]) => onChange({ ...values, [key]: value })

  const headerNameMoved = storedApiKey !== null && storedApiKey.headerName !== values.headerName.trim()

  return (
    <>
      <Form.Group className='mb-3'>
        <Form.Label>Name</Form.Label>
        <Form.Control
          type='text'
          placeholder='Held-away assets'
          value={values.name}
          onChange={(e) => set('name', e.target.value)}
          isInvalid={validated && values.name.trim() === ''}
        />
        <Form.Control.Feedback type='invalid'>A name is required.</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label>
          Description <span className='zocks-text-gray-600'>(optional)</span>
        </Form.Label>
        <Form.Control
          as='textarea'
          rows={2}
          placeholder='Held-away account balances and positions, refreshed nightly.'
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
          isInvalid={validated && !isDescriptionValid(values)}
        />
        <Form.Control.Feedback type='invalid'>Keep the description under {MAX_DESCRIPTION_LENGTH} characters.</Form.Control.Feedback>
        <Form.Text className='zocks-text-gray-600'>What this data source returns, for whoever maintains the app.</Form.Text>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label>Callback URL</Form.Label>
        <Form.Control
          type='url'
          placeholder='https://api.partner.com/zocks/fetch'
          value={values.callbackUrl}
          onChange={(e) => set('callbackUrl', e.target.value)}
          isInvalid={validated && !isHttpsUrl(values.callbackUrl.trim())}
        />
        <Form.Control.Feedback type='invalid'>Must be an HTTPS URL.</Form.Control.Feedback>
        <Form.Text className='zocks-text-gray-600'>HTTPS only, and publicly resolvable. Redirects are not followed.</Form.Text>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label>Data types</Form.Label>
        <DataTypesInput
          dataTypes={values.dataTypes}
          onChange={(dataTypes) => set('dataTypes', dataTypes)}
          isInvalid={validated && values.dataTypes.length === 0}
        />
        <Form.Text className='zocks-text-gray-600'>The keys the data source answers with. The AI asks for these by name.</Form.Text>
      </Form.Group>

      <div className='row'>
        <Form.Group className='col mb-3'>
          <Form.Label>Contract version</Form.Label>
          <Form.Select value={values.contractVersion} onChange={(e) => set('contractVersion', e.target.value)}>
            {SUPPORTED_CONTRACT_VERSIONS.map((version) => (
              <option key={version} value={version}>
                {version}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className='col mb-3'>
          <Form.Label>Timeout (ms)</Form.Label>
          <Form.Control
            type='number'
            value={values.timeoutMs}
            onChange={(e) => set('timeoutMs', e.target.value)}
            isInvalid={validated && !isTimeoutValid(values)}
          />
          <Form.Control.Feedback type='invalid'>
            Between {MIN_TIMEOUT_MS} and {MAX_TIMEOUT_MS} ms.
          </Form.Control.Feedback>
        </Form.Group>
      </div>

      <Form.Group className='mb-2'>
        <Form.Label className='d-flex align-items-center zocks-gap-0.375'>
          Extra credential
          {DOCS_LINKS.declaringAuth ? (
            <a href={DOCS_LINKS.declaringAuth} target='_blank' rel='noreferrer' aria-label='How authentication is declared'>
              <i className='bi bi-question-circle zocks-text-primary' />
            </a>
          ) : null}
        </Form.Label>
        <div className='d-flex zocks-gap-1.25'>
          <Form.Check type='radio' id='eds-auth-none' name='eds-auth' label='None' checked={!values.useApiKey} onChange={() => set('useApiKey', false)} />
          <Form.Check
            type='radio'
            id='eds-auth-api-key'
            name='eds-auth'
            label='API key header'
            checked={values.useApiKey}
            onChange={() => set('useApiKey', true)}
          />
        </div>
        <Form.Text className='zocks-text-gray-600'>Every request is HMAC-signed either way. An API key is only for endpoints that also want one.</Form.Text>
      </Form.Group>

      {values.useApiKey ? (
        <div className='row'>
          <Form.Group className='col mb-3'>
            <Form.Label>Header name</Form.Label>
            <Form.Control
              type='text'
              value={values.headerName}
              onChange={(e) => set('headerName', e.target.value)}
              isInvalid={validated && values.headerName.trim() === ''}
            />
          </Form.Group>
          <Form.Group className='col mb-3'>
            <Form.Label>API key</Form.Label>
            <Form.Control
              type='password'
              placeholder={storedApiKey ? 'Saved — enter a new one to replace it' : ''}
              value={values.apiKey}
              onChange={(e) => set('apiKey', e.target.value)}
              isInvalid={validated && !isAuthValid(values, storedApiKey)}
            />
            <Form.Control.Feedback type='invalid'>
              {headerNameMoved ? 'Re-enter the key to move it to a different header.' : 'An API key is required.'}
            </Form.Control.Feedback>
          </Form.Group>
        </div>
      ) : null}
    </>
  )
}
